'use client';

import {
  type CSSProperties,
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import R2Image from '@/components/ui/R2Image';
import CardWrapper from '@/components/layout/CardWrapper/CardWrapper';

import type { ExpandableCardProps } from './ExpandableCard.types';

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

const setBooleanWhenChanged = (
  setValue: (updater: (current: boolean) => boolean) => void,
  nextValue: boolean,
) => {
  setValue((current) => (current === nextValue ? current : nextValue));
};

type CardContentProps = {
  title: ReactNode;
  description: ReactNode;
  iconUrl?: ExpandableCardProps['iconUrl'];
  iconAlt?: string;
  imageUrl?: ExpandableCardProps['imageUrl'];
  imageAlt?: string;
  clampLines: number;
  expanded?: boolean;
  includeButton?: boolean;
  onToggle?: () => void;
  buttonTabIndex?: -1;
  viewMoreLabel: string;
  viewLessLabel: string;
};

const renderTitle = (title: ReactNode) =>
  typeof title === 'string' ? (
    <h3 className="text-xl leading-snug font-semibold text-gray-950">
      {title}
    </h3>
  ) : (
    <div className="text-xl leading-snug font-semibold text-gray-950">
      {title}
    </div>
  );

const getLineClampStyle = (expanded: boolean, clampLines: number) =>
  expanded
    ? undefined
    : {
        display: '-webkit-box',
        overflow: 'hidden',
        WebkitBoxOrient: 'vertical' as const,
        WebkitLineClamp: clampLines,
      };

const renderDescription = (
  description: ReactNode,
  className: string,
  style?: CSSProperties,
) =>
  typeof description === 'string' ? (
    <p className={className} style={style}>
      {description}
    </p>
  ) : (
    <div className={className} style={style}>
      {description}
    </div>
  );

const CardContent = memo(function CardContent({
  title,
  description,
  iconUrl,
  iconAlt = '',
  imageUrl,
  imageAlt,
  clampLines,
  expanded = false,
  includeButton = false,
  onToggle,
  buttonTabIndex,
  viewMoreLabel,
  viewLessLabel,
}: CardContentProps) {
  const shouldReserveButtonSpace = includeButton && !expanded;

  return (
    <div className="flex h-full min-h-0 w-full flex-col p-5 sm:p-6 md:p-7 lg:p-8">
      <div
        className={[
          'min-h-0 space-y-4',
          shouldReserveButtonSpace ? 'flex-1 overflow-hidden' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {(iconUrl || imageUrl) && (
          <div className="space-y-4">
            {iconUrl && (
              <div className="bg-theme-primary flex h-16 w-16 items-center justify-center rounded-full">
                <R2Image
                  path={iconUrl}
                  alt={iconAlt}
                  width={40}
                  height={40}
                  className="size-10 object-contain"
                  aria-hidden={iconAlt ? undefined : true}
                />
              </div>
            )}

            {imageUrl && (
              <R2Image
                path={imageUrl}
                alt={imageAlt ?? ''}
                width={600}
                height={300}
                className="h-auto w-full rounded-lg object-cover"
              />
            )}
          </div>
        )}

        <div className="space-y-3">
          {renderTitle(title)}
          {renderDescription(
            description,
            'text-base leading-7 text-gray-600',
            getLineClampStyle(expanded, clampLines),
          )}
        </div>
      </div>

      {includeButton && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={onToggle}
          tabIndex={buttonTabIndex}
          className="text-theme-primary hover:text-theme-primary-dark focus-visible:outline-theme-primary w-fit shrink-0 cursor-pointer pt-6 text-sm font-semibold transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
        >
          {expanded ? viewLessLabel : viewMoreLabel}
        </button>
      )}
    </div>
  );
});

const ExpandableCard = ({
  title,
  description,
  iconUrl,
  iconAlt = '',
  imageUrl,
  imageAlt,
  collapsedHeight,
  clampLines = 4,
  className = '',
  measureRef,
  viewMoreLabel = 'View more',
  viewLessLabel = 'View less',
  alwaysExpandable = false,
  disableExpand = false,
}: ExpandableCardProps) => {
  const resolvedClampLines =
    typeof clampLines === 'number' ? clampLines : clampLines.base;
  const [expanded, setExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const fullMeasureRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const fullMeasure = fullMeasureRef.current;

    if (disableExpand || alwaysExpandable) return;

    if (!fullMeasure || collapsedHeight <= 0) {
      return;
    }

    const measureExpandable = () => {
      setBooleanWhenChanged(
        setIsExpandable,
        fullMeasure.scrollHeight > collapsedHeight + 2,
      );
    };

    measureExpandable();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measureExpandable);

    observer?.observe(fullMeasure);
    return () => {
      observer?.disconnect();
    };
  }, [
    collapsedHeight,
    description,
    iconUrl,
    imageUrl,
    alwaysExpandable,
    disableExpand,
    resolvedClampLines,
    title,
  ]);

  const toggleExpanded = useCallback(() => {
    if (disableExpand) return;
    if (!expanded && !alwaysExpandable && !isExpandable) return;

    setExpanded((current) => !current);
  }, [alwaysExpandable, disableExpand, expanded, isExpandable]);

  return (
    <CardWrapper
      columns="one-column"
      width="w-full"
      paddingClassName="p-0"
      className={['relative flex overflow-hidden rounded-lg', className]
        .filter(Boolean)
        .join(' ')}
      style={{
        height: expanded
          ? 'auto'
          : collapsedHeight > 0
            ? `${collapsedHeight}px`
            : undefined,
      }}
    >
      <div
        aria-hidden
        ref={measureRef}
        className="pointer-events-none invisible absolute inset-x-0 top-0"
      >
        <CardContent
          title={title}
          description={description}
          iconUrl={iconUrl}
          iconAlt={iconAlt}
          imageUrl={imageUrl}
          imageAlt={imageAlt}
          clampLines={resolvedClampLines}
          viewMoreLabel={viewMoreLabel}
          viewLessLabel={viewLessLabel}
          buttonTabIndex={-1}
          includeButton
        />
      </div>

      <div
        aria-hidden
        ref={fullMeasureRef}
        className="pointer-events-none invisible absolute inset-x-0 top-0"
      >
        <CardContent
          title={title}
          description={description}
          iconUrl={iconUrl}
          iconAlt={iconAlt}
          imageUrl={imageUrl}
          imageAlt={imageAlt}
          clampLines={resolvedClampLines}
          expanded
          viewMoreLabel={viewMoreLabel}
          viewLessLabel={viewLessLabel}
          buttonTabIndex={-1}
          includeButton={false}
        />
      </div>

      <div className="flex h-full min-h-full w-full overflow-hidden">
        <CardContent
          title={title}
          description={description}
          iconUrl={iconUrl}
          iconAlt={iconAlt}
          imageUrl={imageUrl}
          imageAlt={imageAlt}
          clampLines={resolvedClampLines}
          expanded={expanded}
          includeButton={
            !disableExpand && (expanded || alwaysExpandable || isExpandable)
          }
          onToggle={toggleExpanded}
          viewMoreLabel={viewMoreLabel}
          viewLessLabel={viewLessLabel}
        />
      </div>
    </CardWrapper>
  );
};

export default memo(ExpandableCard);
